import React, { useEffect, useState, useRef } from "react";
import { connect } from "react-redux";
import { getDataFromAPI } from "../../HandleAPICalls/actions";
import { UsersroleList } from "./component";
import { toastr } from "react-redux-toastr";
import _ from "lodash";

const UserPermissionContainer = (props) => {
  const [userList, setUserList] = useState([]);
  const [displayList, setDisplayList] = useState([]);
  const [searchList, setSearchList] = useState(null);
  const [searchData, setSearchData] = useState(null);
  const [start, setStart] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [roleList, setRoleList] = useState([]);
  const [selectedRoleFilter, setSelectedRoleFilter] = useState(null);
  const [selectedRoleName, setSelectedRoleName] = useState("All Roles");
  const [isActive, setIsActive] = useState(true); // Default to active users
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const [filteredCount, setFilteredCount] = useState(0);

  const pageSize = 50;

  // Fetch roles on component mount
  useEffect(() => {
    fetchRoles();
  }, []);

  // Fetch users based on filters
  useEffect(() => {
    fetchUsersList(0, true);
  }, [selectedRoleFilter, isActive]);

  const fetchRoles = () => {
    props.getDataFromAPI(
      "/dashboard/api/v2/admin/users/roles/all",
      "GET",
      undefined,
      (response) => {
        setRoleList(response.list || []);
      },
      (error) => {
        toastr.error("Error", "Failed to load roles");
      }
    );
  };

  const fetchUsersList = (offset, isInitial = false) => {
    if (isInitial) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }

    // Build URL with filters
    let url = `/dashboard/api/v2/admin/users/list?limit=${pageSize}&offset=${offset}`;
    
    // Add role filter if selected
    if (selectedRoleFilter) {
      url += `&role_id=${selectedRoleFilter}`;
    }
    
    // Add status filter if selected (default is active)
    if (isActive !== null) {
      url += `&is_active=${isActive ? 1 : 0}`;
    }

    props.getDataFromAPI(
      url,
      "GET",
      undefined,
      (response) => {
        if (response && response.list) {
          if (offset === 0) {
            setUserList(response.list);
            setDisplayList(response.list);
          } else {
            setUserList(prevList => [...prevList, ...response.list]);
            setDisplayList(prevList => [...prevList, ...response.list]);
          }
          
          setTotalCount(response.totalCount || response.list.length);
          setHasMore(response.list.length === pageSize);
          setStart(offset + pageSize);
          setFilteredCount(response.totalCount || response.list.length);
        } else {
          setHasMore(false);
          if (offset === 0) {
            setUserList([]);
            setDisplayList([]);
            setFilteredCount(0);
          }
        }
        
        setLoading(false);
        setLoadingMore(false);
      },
      (err) => {
        setLoading(false);
        setLoadingMore(false);
        setError(err.message || "Failed to load users");
        toastr.error("Error", err.message || "Failed to load users");
      }
    );
  };

  // Search function
  const Search = (searchData) => {
    setSearchData(searchData);

    if (searchData.trim() === "") {
      setSearchList(null);
      setSearchData(null);
      fetchUsersList(0, true);
    } else {
      props.getDataFromAPI(
        `/dashboard/api/v2/admin/users/search?search=${searchData}`,
        "GET",
        undefined,
        (response) => {
          setSearchList(response || []);
          // Apply current filters to search results
          let filtered = response || [];
          
          if (selectedRoleFilter) {
            filtered = filtered.filter(user => user.role_id === selectedRoleFilter);
          }
          
          if (isActive !== null) {
            filtered = filtered.filter(user => {
              if (isActive === true) {
                return user.status === "active";
              } else {
                return user.status === "deleted" || user.status === "inactive";
              }
            });
          }
          
          setDisplayList(filtered);
          setFilteredCount(filtered.length);
        },
        (err) => {
          toastr.error("Error", "Search failed");
        }
      );
    }
  };

  // Handle role filter
  const handleRoleFilter = (roleId, roleName) => {
    setSelectedRoleFilter(roleId);
    setSelectedRoleName(roleName || "All Roles");
    setStart(0);
    setUserList([]);
    setDisplayList([]);
  };

  // Handle status filter
  const handleStatusFilter = (status) => {
    if (status === "active") {
      setIsActive(true);
    } else if (status === "inactive") {
      setIsActive(false);
    } else if (status === "all") {
      setIsActive(null);
    }
    setStart(0);
    setUserList([]);
    setDisplayList([]);
  };

  const handleActiveClick = () => handleStatusFilter("active");
  const handleInactiveClick = () => handleStatusFilter("inactive");
  const handleAllStatusClick = () => handleStatusFilter("all");

  // Clear all filters
  const handleClearFilters = () => {
    setSelectedRoleFilter(null);
    setSelectedRoleName("All Roles");
    setIsActive(true); // Reset to active users
    setSearchData(null);
    setSearchList(null);
    setStart(0);
    setUserList([]);
    setDisplayList([]);
  };

  // Load more users
  const handleLoadMore = () => {
    if (hasMore && !loadingMore) {
      fetchUsersList(start, false);
    }
  };

  const tableContainerRef = useRef(null);

  return (
    <>
      {error && (
        <div className="ur-error-message">
          <span>{error}</span>
          <button className="ur-error-dismiss" onClick={() => setError(null)}>×</button>
        </div>
      )}
      <UsersroleList
        displayList={displayList}
        Search={Search}
        hasMore={hasMore}
        loading={loading}
        loadingMore={loadingMore}
        onLoadMore={handleLoadMore}
        searchData={searchData}
        roleList={roleList}
        handleRoleFilter={handleRoleFilter}
        handleActiveClick={handleActiveClick}
        handleInactiveClick={handleInactiveClick}
        handleAllStatusClick={handleAllStatusClick}
        handleClearFilters={handleClearFilters}
        isActive={isActive}
        selectedRoleName={selectedRoleName}
        selectedRoleFilter={selectedRoleFilter}
        totalCount={totalCount}
        filteredCount={filteredCount}
        tableContainerRef={tableContainerRef}
      />
    </>
  );
};

function mapStateToProps({ props }) {
  return {
    props,
  };
}

export default connect(mapStateToProps, {
  getDataFromAPI,
})(UserPermissionContainer);